import type { ZodSchema } from "zod";

import { api } from "@/lib/axiosClient";
import { apiUrl } from "@/shared/api/config";
import { normalizeKeysToCamelCase } from "@/shared/lib/zod-normalization";

export interface RequestConfig<TBody = unknown> {
  url: string;
  method?: "get" | "post" | "put" | "patch" | "delete";
  params?: Record<string, string | number | boolean | undefined>;
  data?: TBody;
  headers?: Record<string, string>;
}

const isBrowser =
  typeof window !== "undefined" &&
  typeof navigator !== "undefined" &&
  typeof document !== "undefined";

const buildAbsoluteUrl = (
  base: string | undefined,
  pathOrUrl: string,
  params?: RequestConfig["params"],
) => {
  const isAbsolute = /^https?:\/\//i.test(pathOrUrl);
  const baseUrl = base ?? apiUrl;

  if (isAbsolute) {
    const u = new URL(pathOrUrl);
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (v === undefined) continue;
        u.searchParams.set(k, String(v));
      }
    }
    return u;
  }

  if (!/^https?:\/\//i.test(baseUrl)) {
    throw new Error(
      `Server request requires absolute baseURL, got: ${baseUrl}`,
    );
  }

  const baseU = new URL(baseUrl);
  const basePath = baseU.pathname.replace(/\/$/, "");
  const nextPath = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;
  baseU.pathname = `${basePath}${nextPath}`;

  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v === undefined) continue;
      baseU.searchParams.set(k, String(v));
    }
  }

  return baseU;
};

async function requestServer<TResponse, TBody = unknown>(
  config: RequestConfig<TBody>,
  schema: ZodSchema<TResponse>,
): Promise<TResponse> {
  const { url, method = "get", params, data, headers } = config;
  const u = buildAbsoluteUrl(api.defaults.baseURL, url, params);

  const res = await fetch(u.toString(), {
    method: method.toUpperCase(),
    headers: {
      ...(data ? { "Content-Type": "application/json" } : {}),
      ...(headers ?? {}),
      Accept: "application/json",
    },
    body: data ? JSON.stringify(data) : undefined,
    cache: "no-store",
  });

  const contentType = res.headers.get("content-type") ?? "";
  const isJson = contentType.includes("application/json");
  let payload = isJson
    ? await res.json().catch(() => null)
    : await res.text().catch(() => "");

  if (!res.ok) {
    throw new Error(
      `HTTP ${res.status} ${res.statusText} for ${method.toUpperCase()} ${u.toString()}`,
    );
  }

  if (res.status === 204 || res.status === 205) {
    payload = undefined;
  }

  // Нормализация PascalCase → camelCase для серверных запросов
  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    payload = normalizeKeysToCamelCase(payload as Record<string, unknown>);
  }

  return schema.parse(payload);
}

export async function request<TResponse, TBody = unknown>(
  config: RequestConfig<TBody>,
  schema: ZodSchema<TResponse>,
): Promise<TResponse> {
  if (!isBrowser) {
    return requestServer(config, schema);
  }

  const { url, method = "get", params, data, headers } = config;
  const response = await api.request({ url, method, params, data, headers });
  if (response.status === 204 || response.status === 205) {
    return schema.parse(undefined);
  }
  // Нормализация уже выполнена в Axios interceptor, но для безопасности проверяем
  const normalizedData =
    response.data &&
    typeof response.data === "object" &&
    !Array.isArray(response.data)
      ? normalizeKeysToCamelCase(response.data as Record<string, unknown>)
      : response.data;
  return schema.parse(normalizedData);
}

export async function get<TResponse>(
  url: string,
  schema: ZodSchema<TResponse>,
  params?: RequestConfig["params"],
  headers?: RequestConfig["headers"],
): Promise<TResponse> {
  if (!isBrowser) {
    return requestServer({ url, method: "get", params, headers }, schema);
  }

  const response = await api.request({ url, method: "get", params, headers });
  return schema.parse(response.data);
}

export async function post<TResponse, TBody = unknown>(
  url: string,
  body: TBody,
  schema: ZodSchema<TResponse>,
  headers?: RequestConfig["headers"],
): Promise<TResponse> {
  return request({ url, method: "post", data: body, headers }, schema);
}
